/**
 * MA-THEME-OVERRIDE-151: Shared Theme Adapters
 * 
 * Canonical token transformation functions for converting PP theme
 * to surface-specific token formats.
 * 
 * DO NOT create new transformation logic. These are the ONLY adapters.
 */

/**
 * Convert PP canonical theme to LP token format.
 * Used by: PPB LP Step, LP Builder, any surface needing LP tokens.
 * 
 * Extracted from ProfitPathBuilder.jsx:7061 to make reusable.
 * 
 * @param {Object} profitPathTheme - PP theme object with tokens
 * @returns {Object} LP token format
 */
export function convertThemeToLpTokens(profitPathTheme) {
  if (!profitPathTheme?.tokens) {
    console.error('[MA-287][INVALID THEME] Missing tokens object:', profitPathTheme);
    return null;
  }
  
  const t = profitPathTheme.tokens;
  const mode = profitPathTheme.mode || 'light';
  const isDark = mode === 'dark';
  
  // MA-287: STRICT VALIDATION - Critical tokens MUST exist
  const criticalTokens = {
    primary: t.primary,
    surface_base: t.surface_base,
    surface_elevated: t.surface_elevated,
    text_primary: t.text_primary,
    text_secondary: t.text_secondary,
    border_subtle: t.border_subtle,
    text_on_accent: t.primary_foreground || t.text_inverse,
  };
  
  const missingCritical = Object.entries(criticalTokens)
    .filter(([key, value]) => !value)
    .map(([key]) => key);
  
  if (missingCritical.length > 0) {
    console.error('[MA-287][INVALID THEME] Missing critical tokens:', missingCritical);
    console.error('[MA-287][INVALID THEME] Received theme:', profitPathTheme);
    console.error('[MA-287][INVALID THEME] This theme is incomplete and will cause render failures.');
    console.error('[MA-287][INVALID THEME] Fix the theme generation source (PPB/Profit Path Builder).');
    
    // In development, we could block render here
    if (process.env.NODE_ENV === 'development') {
      console.error('[MA-287][DEV MODE] Returning null to surface the issue');
      return null;
    }
  }
  
  // MA-287: Convert with MINIMAL fallbacks (only for non-critical aesthetic tokens)
  return {
    accent: t.primary,
    surfaceMode: mode,
    // Core surfaces - NO fallbacks (must exist)
    cardBg: t.surface_elevated,
    bgPage: t.surface_base,
    textPrimary: t.text_primary,
    textMuted: t.text_secondary,
    textOnAccent: t.primary_foreground || t.text_inverse,
    borderSubtle: t.border_subtle,
    // Derived/optional tokens - fallbacks acceptable
    accentStrong: t.primary_hover || t.primary,
    // MA-298: surface1 should map to surface_base (thread background = white)
    surface1: t.surface_base || t.surface_subtle || t.surface_elevated,
    textSecondary: t.text_secondary,
    textOnAccentSoft: t.text_on_accent_soft || t.primary_foreground || '#ffffff',
    accentSoft: t.accent_soft || t.primary_soft || (isDark ? 'rgba(139,92,246,0.15)' : 'rgba(139,92,246,0.1)'),
  };
}

/**
 * MA-269: Canonical Funnel Theme Token Resolver
 * 
 * SINGLE SOURCE OF TRUTH for all funnel renderers (PPB, Builder, Standalone).
 * 
 * Precedence model (MA-269):
 * 1. appearance (canonical source of truth per MA-267 backend contract)
 * 2. lpTokens (seed/default layer from PP custom theme)
 * 
 * NO hardcoded fallbacks - missing appearance is a hydration bug to fix.
 * 
 * @param {Object|null} lpTokens - Converted LP tokens from PP custom theme
 * @param {Object} appearance - Funnel appearance (canonical, guaranteed by MA-267)
 * @returns {Object} Resolved theme tokens for all UI elements
 */
export function resolveThemeTokens(lpTokens = null, appearance = {}) {
  const a = appearance || {};
  
  // MA-269: Build intermediate token map with canonical precedence
  // appearance → lpTokens (NO hardcoded fallbacks)
  // MA-278: Corrected light-theme mapping — thread=white, bot bubble=gray
  const c = {
    surface: a.containerColor ?? lpTokens?.cardBg,           // bot bubble → cardBg (gray #e8eef5)
    background: a.threadColor ?? lpTokens?.surface1,         // thread → surface1 (white #ffffff)
    cta: a.userColor ?? a.accent ?? lpTokens?.accent,
    cta_text: a.userTextColor ?? lpTokens?.textOnAccent,
    text: a.botTextColor ?? lpTokens?.textPrimary,
    text_muted: lpTokens?.textMuted,
    border: lpTokens?.borderSubtle,
  };
  
  // MA-305: Light mode calibration (Phase 0 parity with PPB Funnel Step)
  // Apply the same local overrides as FunnelLivePreview for light mode
  const isLightMode = a.surfaceMode === 'light' || lpTokens?.surfaceMode === 'light';
  
  if (isLightMode) {
    // Apply calibrated light theme values (matches PPB Funnel Step exactly)
    return {
      // Intermediate map (exposed for custom derivations)
      _map: c,
      
      // MA-305 Light Mode Calibrated Values (matches PPB Funnel Step local overrides)
      userColor: c.cta,
      containerColor: c.surface,
      threadColor: '#f8fafc',           // Calibrated: Thread background
      botBubbleBg: '#e5e7eb',           // Calibrated: Bot bubble
      botBubbleText: c.text,
      userTextColor: c.cta_text,
      borderColor: c.border,
      textMuted: c.text_muted,
      headerText: c.text,
      
      // MA-305: Semantic tokens (Phase 0 parity)
      responseBg: '#f1f5f9',            // Calibrated: Response area
      headerBg: '#f1f5f9',              // Calibrated: Header
      
      // Input field tokens (matches FB Test Page)
      inputBg: '#F2F2F7',               // Light gray input background
      inputText: '#1C1C1E',             // Dark input text
      inputPlaceholder: '#8E8E93',      // Muted placeholder text
      
      // Legacy aliases for backward compatibility
      botColor: undefined,
      systemColor: undefined,
      prospectColor: undefined,
      headerBackgroundColor: undefined,
      iconBackgroundColor: undefined,
    };
  }
  
  // Dark mode: Use original token derivation (unchanged)
  return {
    // Intermediate map (exposed for custom derivations)
    _map: c,
    
    // Primary UI element colors (appearance → lpTokens, NO hardcoded fallbacks)
    userColor: c.cta,
    containerColor: c.surface ?? c.background,
    threadColor: c.background,
    botBubbleBg: c.surface,
    botBubbleText: c.text,
    userTextColor: c.cta_text,
    borderColor: c.border,
    textMuted: c.text_muted,
    headerText: c.text,
    
    // MA-305: Response area semantic token (Phase 0 parity)
    responseBg: a.responseAreaColor ?? c.surface ?? c.background,
    headerBg: a.headerBackgroundColor ?? c.surface ?? c.background,
    
    // Input field tokens (matches FB Test Page dark mode)
    inputBg: 'rgba(255, 255, 255, 0.08)',   // Subtle light overlay on dark
    inputText: c.text,                       // Light text for dark mode
    inputPlaceholder: c.text_muted,          // Muted text
    
    // Legacy aliases for backward compatibility
    botColor: undefined,  // Computed as botBubbleBg above
    systemColor: undefined,  // Computed as botBubbleBg above
    prospectColor: undefined,  // Computed as userColor above
    headerBackgroundColor: undefined,  // Use containerColor instead
    iconBackgroundColor: undefined,  // Use userColor instead
  };
}

/**
 * Re-export themeToFunnelAppearance from themeEngine for convenience.
 * This is the canonical funnel appearance adapter.
 */
export { themeToFunnelAppearance } from './themeEngine';
